/**
 * WHO OWES A MONTHLY AWARD PICK, AND WHAT THEY ARE TOLD. Spelled once for the
 * morning reminder (netlify/functions/studentAwardsReminder.mjs) and for
 * Kim's review board and Post congratulations card on /student-awards, because
 * the reminder and the board must name the same people (docs/lessons.md: a
 * count computed from a fresh filter beside a list built from a different one
 * disagrees by one, on screen, at once). WHEN they are told is
 * lib/studentAwardSchedule.ts.
 *
 * Bobbie, 2026-09-28 morning Central, verbatim: "also a reminder for dancer of
 * the month should have gone out with instructions on how to do it for all
 * teachers managers directors who teach a class and all JR staff. these should
 * be automatic 5 days before the last day of the month and then a reminder
 * again to anyone who has not done it at the 2 day mark and the 1 day mark.
 * then dialy if they have note done it until it is done" and "and a it should
 * be a task". On 2026-10-26 Bobbie moved the open day to 15 days before the
 * month's last day, from the October 2026 round on ("open 15 days before the
 * end of the month so teachers can vote on all of the monthly awards"), and
 * the to-do to 10 days before it ("Can we do the TO DO AT THR 10 day mark?");
 * AWARD_OPENS_DAYS_BEFORE and AWARD_FIRST_REMINDER_DAYS_BEFORE in
 * lib/studentAwardSchedule.ts carry them.
 *
 * WHO. Student of the Month is owed by every active Executive Teacher
 * ('company'), Manager and Director who TEACHES at least one current class, as
 * lead or co-teacher. Someone who teaches nothing owes nothing, and a Director
 * who teaches now owes a pick like anyone else. The Monthly Spotlight is owed
 * by every active Jr Staff, ONE entry per human however many sign-ins she
 * holds (CLAUDE.md, "A Jr Staff is ONE PERSON, however she signs in"): a pick
 * on either sign-in counts for her, and the to-do is keyed to the one uid the
 * lists show (her email account when she has one, the shared resolver's
 * precedence in lib/jrStaffAliases.ts, not a new one written here).
 *
 * WHICH MONTH THE WORDS NAME. The month being RECOGNIZED, never the stored
 * period key (Bobbie, 2026-09-28: "they are nomiated for the previous
 * month"); see recognizedAwardMonthLabel in lib/studentAwardSchedule.ts.
 *
 * PURE ON PURPOSE. The imports are the shared Jr Staff alias module and the
 * schedule, both Firebase-free, so a Netlify Function and a node test can load
 * this file directly under --experimental-strip-types.
 */

import { jrStaffAliasGroups, type AliasAccount } from './jrStaffAliases.ts';
import {
  awardDueDateKey,
  awardLongDateLabel,
  awardNominationCloseDateKey,
  awardShortDateLabel,
  recognizedAwardMonthLabel,
  recognizedMonthFirstDateKey,
  type AwardReminderStep,
} from './studentAwardSchedule.ts';

export type AwardType = 'student-of-month' | 'jr-spotlight';

export const AWARD_TYPES: readonly AwardType[] = ['student-of-month', 'jr-spotlight'];

/**
 * The award names, one place. Bobbie, 2026-09-29 about 3:50pm Central, chose
 * "Spotlight of the Month" everywhere ("currently the spotligith and spolight
 * of the month look diffeent but they should be the same"), one colour, blue.
 * "Monthly Spotlight" is retired; the morning reminder's one-off relabel of
 * the September 2026 to-dos still matches the old stored title by its own
 * frozen copy (studentAwardsReminder.mjs BASE_CODE_AWARD_LABELS).
 *
 * And the other award is "Dancer of the Month" (Bobbie, 2026-10-01 about
 * 2:40pm Central: "She wants it to be Dancer of the month ... Never student.";
 * Kim: "student of the month feels very school to me ... I like it saying
 * dancer of the month"). Only the words changed: the stored key is still
 * 'student-of-month', so no data moved (DECISIONS 91).
 */
export const AWARD_TYPE_LABELS: Record<AwardType, string> = {
  'student-of-month': 'Dancer of the Month',
  'jr-spotlight': 'Spotlight of the Month',
};

/**
 * Mirrors canNominateAwardType() in shared/firestore.rules byte for byte in
 * meaning: the rules are the permission, this only keeps a screen and the
 * reminder from asking someone for a pick the rules would refuse.
 */
const NOMINATING_ROLES: Record<AwardType, readonly string[]> = {
  'student-of-month': ['company', 'director', 'manager'],
  'jr-spotlight': ['ensemble', 'director'],
};

export function awardRoleCanNominate(role: string | null | undefined, awardType: AwardType): boolean {
  if (typeof role !== 'string') return false;
  return NOMINATING_ROLES[awardType].includes(role);
}

/* ============================================================================
 * The written reason
 * ==========================================================================*/

/**
 * Kim, 2026-09-28, verbatim: "for senior staff, I need a sentence why they
 * were picked, not just the name. The junior staff can just be the name." and
 * "yours for example was just a name...So it needs to make you write a
 * sentence." So an Executive Teacher, Manager or Director writes at least
 * this many characters (after trimming) before a pick can be sent; a Jr Staff
 * may send the name alone. The server (netlify/functions/studentAwardAction.mjs
 * and the attachment gateway) refuses a short one, not just the button.
 */
export const AWARD_WHY_MIN_CHARS = 15;
/** The longest note a pick may carry, the same cap the save paths already trim to. */
export const AWARD_WHY_MAX_CHARS = 1000;
/** The longest reason Kim may give when she sends a pick back. */
export const AWARD_RETURN_REASON_MAX_CHARS = 500;

/**
 * KIM'S SEND-BACK REACHES THE NOMINATOR ONLY. A Jr Staff's parents are never
 * told a pick came back, on any sign-in shape (Bobbie, 2026-09-28: "no parets
 * should not get jr staff alerts", DECISIONS 64), so there is no parent copy
 * and no switch for one. netlify/functions/studentAwardAction.mjs sendBack.
 */

/**
 * DOES THE JR STAFF MVP WIN NOTICE REACH HER PARENTS? YES, AND THIS IS THE ONE LINE.
 *
 * Bobbie's MVP answers (DECISIONS 52, the morning of 2026-09-28) said the
 * winner and her parents are told. Her later ruling the same day, "no parets
 * should not get jr staff alerts" (DECISIONS 64), left this notice open. She
 * answered on 2026-09-29 about 4pm Central, verbatim: "Yes, tell her parents".
 * So the MVP notice is the exception: when a Director puts the month's
 * winners on the wall, she is told on every sign-in she holds AND her parents
 * are told. It goes out only from the wall step, which holds it back while
 * award announcements are paused (lib/wallPause.ts AWARD_ANNOUNCEMENTS_PAUSED,
 * netlify/functions/lib/wallNoticePause.mjs). Every other Jr Staff alert still
 * reaches no parent.
 */
export const MVP_WIN_NOTICE_COPIES_PARENTS = true;

const WHY_REQUIRED_ROLES: readonly string[] = ['company', 'manager', 'director'];

/** Does a nominator in this role have to write why? */
export function awardWhyRequired(role: string | null | undefined): boolean {
  return typeof role === 'string' && WHY_REQUIRED_ROLES.includes(role);
}

/** Is this note enough for this nominator? A Jr Staff's may be empty. */
export function awardWhyIsEnough(role: string | null | undefined, note: unknown): boolean {
  if (!awardWhyRequired(role)) return true;
  return typeof note === 'string' && note.trim().length >= AWARD_WHY_MIN_CHARS;
}

/* ============================================================================
 * Who teaches a current class
 * ==========================================================================*/

/** The classSeries fields this reads, and nothing else. */
export interface AwardSeriesTerm {
  offeringId?: unknown;
  termStart?: unknown;
  termEnd?: unknown;
}

/**
 * Is every dated term of this class over? The same term arithmetic
 * offeringTermStatus (lib/teachSchedule.ts) uses to call a class 'ended':
 * only when every dated term has finished. A class with no dated series at
 * all is dated by its bookings there; this does not read bookings (that would
 * be a reservation query per class every morning), so an undated class is not
 * over here, which is offeringTermStatus's own answer for a class whose
 * bookings have not been looked up ('none', never 'ended').
 * tests/award-reminder-schedule.test.mjs runs the two side by side.
 */
export function offeringTermIsOver(series: readonly AwardSeriesTerm[], todayKey: string): boolean {
  const dated = series.filter((s) => (
    typeof s.termStart === 'string' && s.termStart !== ''
    && typeof s.termEnd === 'string' && s.termEnd !== ''
  ));
  if (dated.length === 0) return false;
  return dated.every((s) => (s.termEnd as string) < todayKey);
}

export interface AwardOfferingInput {
  id: string;
  status?: unknown;
}

/**
 * Every uid who leads or co-teaches at least one CURRENT class today. The
 * caller hands in its own teacher-uid helper (the Function's
 * lib/offeringTeachers.mjs teacherUidsForOffering, the page's
 * lib/offeringTeachers.ts offeringTeacherUids), both lead then co-teachers.
 * A class switched off (status other than 'active', dancerPortal.mjs's own
 * test for a current class) or whose every term has ended does not count.
 */
export function currentTeachingUids<T extends AwardOfferingInput>(input: {
  offerings: readonly T[];
  series: readonly AwardSeriesTerm[];
  todayKey: string;
  teacherUidsOf: (offering: T) => readonly string[];
}): Set<string> {
  const uids = new Set<string>();
  for (const offering of currentAwardOfferings(input)) {
    for (const uid of input.teacherUidsOf(offering)) {
      if (typeof uid === 'string' && uid) uids.add(uid);
    }
  }
  return uids;
}

/**
 * The classes that are current today, by the same two tests: switched on
 * (status 'active', or none) and not every dated term over. The Jr Staff MVP
 * list reads it to put a teacher's own Jr Staff first.
 */
export function currentAwardOfferings<T extends AwardOfferingInput>(input: {
  offerings: readonly T[];
  series: readonly AwardSeriesTerm[];
  todayKey: string;
}): T[] {
  const seriesByOffering = new Map<string, AwardSeriesTerm[]>();
  for (const row of input.series) {
    const offeringId = typeof row.offeringId === 'string' ? row.offeringId : '';
    if (!offeringId) continue;
    const list = seriesByOffering.get(offeringId);
    if (list) list.push(row);
    else seriesByOffering.set(offeringId, [row]);
  }
  return input.offerings.filter((offering) => {
    if (!offering || typeof offering.id !== 'string' || !offering.id) return false;
    const status = offering.status ?? 'active';
    if (status !== 'active') return false;
    return !offeringTermIsOver(seriesByOffering.get(offering.id) ?? [], input.todayKey);
  });
}

/**
 * The day "who teaches" is asked on for one round: the first day of the month
 * it RECOGNIZES. Codex review of #1252, P1: asking it of each chase morning
 * instead dropped a teacher whose class term ended during the window off the
 * roster, which stopped her reminders and let "every monthly pick is in" fire
 * without her pick. A class counts for a round when it was not already over
 * before that month began, so the teacher who taught it through September
 * owes a September pick however late in the chase it is. (A class switched
 * off mid-window is the other half, and the round's stored roster below
 * answers it.)
 */
export function awardTeachingAsOfKey(period: string): string {
  return recognizedMonthFirstDateKey(period);
}

/* ============================================================================
 * The round's roster, kept once it is set
 * ==========================================================================*/

/**
 * Where a round's roster is kept: studioSettings/awardRoundRoster_<period>.
 * Written only by the morning reminder (Admin SDK); every active staff
 * account can already read studioSettings, so Kim's board reads the very list
 * the reminder chased from, with no rules byte changed.
 */
export const AWARD_ROUND_ROSTER_COLLECTION = 'studioSettings';
export function awardRoundRosterDocId(period: string): string {
  return `awardRoundRoster_${period}`;
}

/** The stored roster, as read back. Unknown shapes read as empty. */
export interface AwardRoundRosterSnapshot {
  /** Every Student of the Month nominator's uid the round has held. */
  teachingUids?: unknown;
  /** Every sign-in of every Monthly Spotlight nominator the round has held. */
  spotlightUids?: unknown;
}

function stringSet(value: unknown): Set<string> {
  const out = new Set<string>();
  if (!Array.isArray(value)) return out;
  for (const item of value) {
    if (typeof item === 'string' && item.trim()) out.add(item.trim());
  }
  return out;
}

/**
 * Can this round's roster still GROW today? Through its due date, yes: a
 * teacher who picks up a class or a newly promoted Jr Staff before the pick is
 * due owes one. After it the roster is what it was, so a teacher hired in
 * November is never chased for September.
 */
export function awardRosterIsOpen(period: string, todayKey: string): boolean {
  const due = awardDueDateKey(period);
  return !!due && todayKey <= due;
}

/**
 * WHO OWES THIS ROUND TODAY, for the reminder and Kim's board alike.
 *
 * Codex review of #1252, P1: the roster was recomputed every chase morning
 * from that morning's classes, so a teacher whose class was switched off or
 * whose term ended during the window vanished from it: her reminders stopped,
 * and the Director's "every monthly pick is in" to-do could fire without her.
 * Now a round keeps every nominator it has ever held (the stored roster), adds
 * newcomers only through its due date, and asks "who teaches" as of the first
 * day of the recognized month (awardTeachingAsOfKey). Everyone still has to be
 * an active account the rules let nominate (canCastPick), so someone who left
 * the studio is never chased.
 *
 * With no stored roster yet (the reminder's first look at a round), today's
 * answer is used whatever the date, and becomes the stored roster.
 */
export function awardRoundNominators(input: {
  period: string;
  todayKey: string;
  users: readonly AwardNominatorAccount[];
  /** Who teaches as of awardTeachingAsOfKey(period). Only read while the roster is open or unset. */
  teachingUids: ReadonlySet<string>;
  snapshot: AwardRoundRosterSnapshot | null;
}): {
  nominators: AwardNominator[];
  /** The roster to keep, sorted, and whether it differs from the one stored. */
  next: { teachingUids: string[]; spotlightUids: string[] };
  changed: boolean;
} {
  const storedTeaching = stringSet(input.snapshot?.teachingUids);
  const storedSpotlight = stringSet(input.snapshot?.spotlightUids);
  const frozen = input.snapshot !== null && !awardRosterIsOpen(input.period, input.todayKey);
  const teaching = new Set(storedTeaching);
  if (!frozen) for (const uid of input.teachingUids) teaching.add(uid);
  let nominators = awardNominators({ users: input.users, teachingUids: teaching });
  if (frozen) {
    nominators = nominators.filter((nominator) => (
      nominator.awardType === 'student-of-month' || nominator.uids.some((uid) => storedSpotlight.has(uid))
    ));
  }
  const nextTeaching = new Set(storedTeaching);
  const nextSpotlight = new Set(storedSpotlight);
  if (!frozen) {
    for (const nominator of nominators) {
      const into = nominator.awardType === 'student-of-month' ? nextTeaching : nextSpotlight;
      for (const uid of nominator.uids) into.add(uid);
    }
  }
  const sorted = (set: Set<string>) => [...set].sort((a, b) => a.localeCompare(b));
  const next = { teachingUids: sorted(nextTeaching), spotlightUids: sorted(nextSpotlight) };
  const changed = input.snapshot === null
    || next.teachingUids.length !== storedTeaching.size
    || next.spotlightUids.length !== storedSpotlight.size;
  return { nominators, next, changed };
}

/**
 * WHO A PAST ROUND ASKED, as the round stored it, for the Director's reports
 * ("Teachers with no pick" and the on-time share). Codex P2 on #1283: the
 * reports ran awardRoundNominators, which keeps only accounts active TODAY,
 * so a teacher who left in November vanished from September's history and
 * her missed pick with her. A report is history, so every uid the round
 * stored stays, whatever its account's status now.
 *
 * Student of the Month: one person per stored uid. The Spotlight stores every
 * sign-in of every Jr Staff, so those fold into one person each: the uid
 * today's roster shows her under when she is still on it, otherwise sign-ins
 * that carry the same dancer link are one person, her email account shown
 * first. Only stored Spotlight uids are ever grouped, so a parent account
 * that shares the dancer link is never pulled in. Only ever read for a round
 * whose roster has frozen (awardRosterIsOpen false); an open round asks the
 * people it would chase.
 */
export function awardFrozenRoundNominators(input: {
  snapshot: AwardRoundRosterSnapshot;
  users: readonly AwardNominatorAccount[];
}): AwardNominator[] {
  const byUid = new Map<string, AwardNominatorAccount>();
  for (const user of input.users) {
    const uid = text(user?.uid);
    if (uid && !byUid.has(uid)) byUid.set(uid, user);
  }
  const sorted = (list: Iterable<string>) => [...list].sort((a, b) => a.localeCompare(b));
  const studentOfMonth: AwardNominator[] = sorted(stringSet(input.snapshot.teachingUids))
    .map((uid) => ({ awardType: 'student-of-month' as const, uid, uids: [uid] }));

  const stored = stringSet(input.snapshot.spotlightUids);
  const liveKey = nominatorUidMap(awardNominators({ users: input.users, teachingUids: new Set() }));
  const groups = new Map<string, string[]>();
  for (const uid of sorted(stored)) {
    // The dancer link first (every sign-in of one Jr Staff carries it), then
    // the person today's roster folds her under, then the uid alone.
    const studentId = text(byUid.get(uid)?.studentId);
    const live = liveKey.get(uid);
    const key = studentId ? `student:${studentId}` : live ? `live:${live}` : `uid:${uid}`;
    const group = groups.get(key) ?? [];
    group.push(uid);
    groups.set(key, group);
  }
  const isEmailAccount = (uid: string) => {
    const account = byUid.get(uid);
    return !!account && account.jrStaffPromoted !== true && text(account.role) === 'ensemble';
  };
  const spotlight: AwardNominator[] = [...groups.values()].map((uids) => {
    const shownToday = uids.map((uid) => liveKey.get(uid)).find((live) => !!live && uids.includes(live));
    const first = shownToday ?? uids.find(isEmailAccount) ?? uids[0];
    return { awardType: 'jr-spotlight' as const, uid: first, uids: [first, ...uids.filter((uid) => uid !== first)] };
  });
  spotlight.sort((a, b) => a.uid.localeCompare(b.uid));
  return [...studentOfMonth, ...spotlight];
}

/* ============================================================================
 * Who owes a pick
 * ==========================================================================*/

/** The users fields this reads. Narrow, so a partial row still type-checks. */
export interface AwardNominatorAccount {
  uid: string;
  role?: string | null;
  status?: string | null;
  studentId?: string | null;
  jrStaffPromoted?: boolean;
}

export interface AwardNominator {
  awardType: AwardType;
  /**
   * The ONE uid the to-do is keyed to and the lists show. For a Jr Staff with
   * two sign-ins it is her email account (lib/jrStaffAliases.ts precedence).
   */
  uid: string;
  /** Every sign-in this human can nominate from, `uid` first. A pick on any of them counts. */
  uids: string[];
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function canCastPick(account: AwardNominatorAccount | undefined, awardType: AwardType): boolean {
  return !!account && text(account.status) === 'active' && awardRoleCanNominate(text(account.role), awardType);
}

/**
 * Everyone who owes a pick this round, Student of the Month first, then the
 * Monthly Spotlight, each sorted by uid so the list is stable run to run.
 *
 * The Spotlight half folds sign-ins through jrStaffAliasGroups, the shared
 * resolver, and keeps only the sign-ins that can actually CAST a pick (an
 * active account the rules let nominate): a designated dancer whose only
 * sign-in is an unpromoted dancer login has no session that could send one,
 * so nudging her would ask for something the rules refuse.
 */
export function awardNominators(input: {
  users: readonly AwardNominatorAccount[];
  teachingUids: ReadonlySet<string>;
}): AwardNominator[] {
  const byUid = new Map<string, AwardNominatorAccount>();
  for (const user of input.users) {
    const uid = text(user?.uid);
    if (uid && !byUid.has(uid)) byUid.set(uid, user);
  }

  const studentOfMonth: AwardNominator[] = [...byUid.values()]
    .filter((user) => canCastPick(user, 'student-of-month') && input.teachingUids.has(text(user.uid)))
    .map((user) => text(user.uid))
    .sort((a, b) => a.localeCompare(b))
    .map((uid) => ({ awardType: 'student-of-month' as const, uid, uids: [uid] }));

  const aliasAccounts: AliasAccount[] = [...byUid.values()].map((user) => ({
    uid: text(user.uid),
    role: text(user.role),
    status: text(user.status),
    studentId: text(user.studentId) || null,
    jrStaffPromoted: user.jrStaffPromoted === true,
  }));
  const spotlight: AwardNominator[] = [];
  const seen = new Set<string>();
  for (const group of jrStaffAliasGroups(aliasAccounts)) {
    const everyUid = [group.accountUid, ...group.duplicateUids];
    const casting = everyUid.filter((uid) => canCastPick(byUid.get(uid), 'jr-spotlight'));
    if (casting.length === 0) continue;
    const uid = casting.includes(group.accountUid) ? group.accountUid : casting[0];
    if (seen.has(uid)) continue;
    const others = casting.filter((other) => other !== uid).sort((a, b) => a.localeCompare(b));
    for (const one of [uid, ...others]) seen.add(one);
    spotlight.push({ awardType: 'jr-spotlight', uid, uids: [uid, ...others] });
  }
  spotlight.sort((a, b) => a.uid.localeCompare(b.uid));
  return [...studentOfMonth, ...spotlight];
}

/**
 * The uid whose live pick satisfies this nominator, or null while none has
 * one. `hasLivePick(uid)` answers for one sign-in: a nomination doc for this
 * round and award exists under that uid and Kim has not sent it back.
 */
export function nominatorPickUid(
  nominator: AwardNominator,
  hasLivePick: (uid: string) => boolean
): string | null {
  return nominator.uids.find((uid) => hasLivePick(uid)) ?? null;
}

/** Every uid a nominator list covers, pointing at the one the lists show. */
export function nominatorUidMap(nominators: readonly AwardNominator[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const nominator of nominators) {
    for (const uid of nominator.uids) {
      if (!out.has(uid)) out.set(uid, nominator.uid);
    }
  }
  return out;
}

/**
 * The key one PERSON is counted under, for a Set that must not count a Jr
 * Staff's two sign-ins as two people. A counting key only, never a label: a
 * uid the roster does not know is its own key.
 */
export function nominatorCountingKey(map: ReadonlyMap<string, string>, uid: string): string {
  const mapped = map.get(uid);
  return mapped === undefined ? uid : mapped;
}

/**
 * Does a pick already sent from one of her OTHER sign-ins refuse this one?
 * Yes for a live pick (waiting on Kim or approved) for the same round and
 * award; no for a pick Kim sent back. A Jr Staff is ONE person however she
 * signs in (Codex review of #1252, P1), so she sends one pick a round. The
 * page's save runs this against netlify/functions/awardNominationSiblings.mjs;
 * the attachment gateway runs the same test on its own reads.
 */
export function siblingPickBlocksSave(
  picks: readonly { awardType: string; status: string }[],
  awardType: AwardType
): boolean {
  return picks.some((pick) => pick.awardType === awardType && isLiveAwardPick(pick.status));
}

/**
 * NO PICK THIS MONTH (Bobbie, 2026-11-01, DECISIONS 170). The nominator's own
 * answer that she has no dancer to put forward, written on her nomination doc
 * by netlify/functions/studentAwardAction.mjs noPick and nowhere else. It
 * names no dancer, so it is never a pick: not on the Director's tiles, not
 * on the wall, not in the tally. It IS an answer: the to-do, the morning
 * reminder (studentAwardsReminder.mjs) and the send-back lane all read it as
 * answered, and a real pick sent later, while the round is open, replaces it.
 */
export const AWARD_NO_PICK_STATUS = 'no-pick';

/**
 * Is this status a pick that stands (waiting on a Director, or approved)?
 * False for a pick a Director sent back and for No pick this month. The one
 * reading every "is her pick in" question shares: the save guard above, the
 * nominate lane's sibling check, the Director's N of M, the stray merge.
 */
export function isLiveAwardPick(status: string | null | undefined): boolean {
  return status !== 'rejected' && status !== AWARD_NO_PICK_STATUS;
}

/* ============================================================================
 * The words
 * ==========================================================================*/

/* ----------------------------------------------------------------------------
 * The Jr Staff MVP rides the Student of the Month to-do
 * --------------------------------------------------------------------------*/

/**
 * Bobbie, 2026-09-28, dictated: "each month executive teachers, managers, and
 * directors that teach a class nominate a junior staff MVP" and "I feel like
 * it should have a reminder for both, like my to-dos". So the person who owes
 * a Student of the Month pick owes a Jr Staff MVP vote too, on ONE to-do,
 * done when both are in; choosing No Jr Staff counts as the vote.
 */
export const JR_STAFF_MVP_LABEL = 'Jr Staff MVP';

/**
 * The first round whose to-do and reminders carry the MVP vote: the round
 * stored under 2026-11, recognizing October 2026, open from October 16 with
 * its to-do day October 21 (fifteen and ten days before the month ends,
 * Bobbie 2026-10-26). Both days had passed before either rule was live, so
 * its to-dos are first written by the first morning reminder after the
 * 2026-10-26 deploys, or by a Director's Add missing to-dos before it. The
 * September 2026 round (2026-10) went out on
 * September 28 as Student of the Month alone and its to-dos are live data
 * that were already ticked by the people who picked, so the MVP card is open
 * for September on the page but nobody is chased for it and no to-do changes.
 * Not MONTH_END_AWARD_START_PERIOD: that moved to 2026-10 when the September
 * round got the month-end cadence, and the MVP must not follow it there.
 */
export const MVP_REMINDER_START_PERIOD = '2026-11';

/** Does this round's Student of the Month to-do also ask for the MVP vote? */
export function mvpJoinsAwardTodo(awardType: AwardType, period: string): boolean {
  return awardType === 'student-of-month' && period >= MVP_REMINDER_START_PERIOD;
}

/** The MVP vote's own doc id: one per nominator per round, like a pick. */
export function mvpNominationId(period: string, nominatorUid: string): string {
  return `${period}_${nominatorUid}`;
}

/**
 * DO A JR STAFF'S PARENTS GET HER AWARD NUDGES? NO. BOBBIE RULED IT.
 *
 * Bobbie, 2026-09-28: "no parets should not get jr staff alerts", after
 * "Junior staff, parents should not be getting notifications about the
 * junior staff board, only the junior staff." (her spelling, kept as typed).
 *
 * Before this line the answer depended on how the Jr Staff signs in: a dancer
 * sign-in has no inbox of its own, so notify.mjs's email lane forwarded the
 * staff wording ("your pick", a /student-awards link a family account cannot
 * open) to her parents every morning of a round, while the parents of a Jr
 * Staff on an email account got nothing. Now every shape is treated the same
 * (netlify/functions/studentAwardsReminder.mjs): with this false, no parent
 * hears any award nudge in any shape, the dancer sign-in's email lane
 * included, and the Jr Staff herself is still reached on every sign-in she
 * has (the in-app card and push on each). The true branch (her parents get
 * their own off-app copy, worded with her name, once a day) is kept only so
 * the switch stays one line and both settings stay tested; it is not the
 * ruled behavior, and flipping it needs Bobbie to reverse herself.
 */
export const AWARD_REMINDER_COPIES_JR_STAFF_PARENTS = false;

/** What a Jr Staff's parents read when AWARD_REMINDER_COPIES_JR_STAFF_PARENTS is on. */
export function jrStaffParentReminderCopy(input: {
  jrStaffName: string;
  awardType: AwardType;
  period: string;
}): { title: string; body: string } {
  const label = AWARD_TYPE_LABELS[input.awardType];
  const month = recognizedAwardMonthLabel(input.period);
  const due = awardDueDateKey(input.period);
  const who = input.jrStaffName.trim() || 'Your Jr Staff';
  return {
    title: `${who}'s ${label} pick for ${month}`,
    body: `${who} has a ${label} pick to send for ${month}${due ? `, due ${awardShortDateLabel(due)}` : ''}. It is sent from Student Awards in the Hub.`,
  };
}

/**
 * The steps, matching pages/StudentAwards.tsx as it stands (Bobbie's design
 * of 2026-09-29): the to-do and the reminder open the page on the right
 * month; the forms sit on the My Nominations tab, which a Director has too
 * (her page opens on This Month); then Who?, Why them?, and the Send
 * nomination button; then, where the round asks for it, the Jr Staff MVP card
 * and its Send MVP vote button (pages/studentAwards/MvpNominationCard.tsx).
 * Staff words say "a Director", never a name (Bobbie, 2026-09-29: "Let's make
 * it for directors, not just Kim. In case she needs to delegate that.").
 * Change a label there, change it here.
 */
export function awardNominationSteps(options: {
  forDirector?: boolean;
  whyRequired?: boolean;
  withMvp?: boolean;
} = {}): string[] {
  return [
    'Tap Open Student Awards on this to-do.',
    'Tap My Nominations at the top.',
    "Under Who?, search the dancer's name and tap it.",
    options.whyRequired
      ? 'Under Why them?, write a sentence about why.'
      : 'Under Why them?, add a few words if you like.',
    'Tap Send nomination.',
    ...(options.withMvp
      ? [
        'Under Jr Staff MVP, tap your pick (your own Jr Staff are listed first) or No Jr Staff.',
        'Tap Send MVP vote.',
      ]
      : []),
  ];
}

export function awardTaskCopy(
  awardType: AwardType,
  period: string,
  options: { forDirector?: boolean } = {}
): { title: string; description: string } {
  const label = AWARD_TYPE_LABELS[awardType];
  // The RECOGNIZED month, never the stored period key: the round stored under
  // 2026-11 is the October 2026 award (lib/studentAwardSchedule.ts).
  const month = recognizedAwardMonthLabel(period);
  const withMvp = mvpJoinsAwardTodo(awardType, period);
  const forDirector = options.forDirector === true;
  const steps = awardNominationSteps({
    forDirector,
    // Student of the Month is only ever owed by teachers, managers and
    // directors, who write why; a Spotlight to-do goes to Jr Staff.
    whyRequired: awardType === 'student-of-month' || forDirector,
    withMvp,
  })
    .map((step, index) => `${index + 1}. ${step}`)
    .join(' ');
  const due = awardDueDateKey(period);
  const closes = awardNominationCloseDateKey(period);
  const intro = withMvp
    ? `Two picks for ${month}: one dancer for ${label}, and one ${JR_STAFF_MVP_LABEL} (or No Jr Staff).`
    : `Pick one dancer to recognize for ${month}.`;
  return {
    title: withMvp ? `Nominate ${label} and ${JR_STAFF_MVP_LABEL} for ${month}` : `Nominate ${label} for ${month}`,
    description:
      `${intro} ${steps}`
      + (due ? ` Due ${awardLongDateLabel(due)}.` : '')
      + (closes ? ` Nominations close ${awardLongDateLabel(closes)} at 11:59pm.` : ''),
  };
}

/** What a reminder still asks for. Defaults to the pick alone. */
export interface AwardReminderMissing {
  pick: boolean;
  mvp: boolean;
}

function missingPhrase(label: string, missing: AwardReminderMissing): string {
  if (missing.pick && missing.mvp) return `${label} and ${JR_STAFF_MVP_LABEL} picks`;
  if (missing.mvp) return `${JR_STAFF_MVP_LABEL} vote`;
  return `${label} pick`;
}

/**
 * One reminder's words. The in-app card and the push, email and text all say
 * the same thing; only the first one carries the steps.
 */
export function awardReminderCopy(
  awardType: AwardType,
  period: string,
  step: AwardReminderStep,
  options: { forDirector?: boolean; daysLeft?: number; missing?: AwardReminderMissing } = {}
): { title: string; body: string } {
  const label = AWARD_TYPE_LABELS[awardType];
  const month = recognizedAwardMonthLabel(period);
  const due = awardDueDateKey(period);
  const dueText = due ? awardShortDateLabel(due) : '';
  const missing = options.missing ?? { pick: true, mvp: false };
  const what = missingPhrase(label, missing);
  // Kim's close day (AWARD_NOMINATIONS_CLOSE_DAY): said on every reminder, so
  // nobody learns about it by finding the form shut. The Jr Staff MVP vote
  // rides the same to-do and closes the same minute.
  const closes = awardNominationCloseDateKey(period);
  const closeText = closes ? ` Nominations close ${awardShortDateLabel(closes)} at 11:59pm.` : '';
  const dueAndClose = `${dueText}${closes ? `, closes ${awardShortDateLabel(closes)}` : ''}`;
  if (step === 'first') {
    const director = options.forDirector === true ? ' tap My Nominations,' : '';
    // The phone copy is cut at 180 characters, so every first one names the
    // close day in its opening words instead of adding a sentence.
    if (missing.mvp && !missing.pick) {
      return {
        title: `Time to vote for ${JR_STAFF_MVP_LABEL} for ${month}`,
        body:
          `Due ${dueAndClose}. Open Student Awards, then under ${JR_STAFF_MVP_LABEL} tap your pick or No Jr Staff `
          + 'and tap Send MVP vote.',
      };
    }
    if (missing.mvp) {
      return {
        title: `Time to nominate ${label} and ${JR_STAFF_MVP_LABEL} for ${month}`,
        body:
          `Both are due ${dueAndClose}. Open Student Awards,${director} send your dancer with a sentence of why, `
          + 'then pick your Jr Staff MVP or No Jr Staff.',
      };
    }
    return {
      title: `Time to nominate ${label} for ${month}`,
      body:
        `Due ${dueAndClose}. Open Student Awards,${director} `
        + 'search the dancer under Who?, add why under Why them?, then tap Send nomination.',
    };
  }
  const capitalized = `${what[0].toUpperCase()}${what.slice(1)}`;
  if (step === 'reminder') {
    const when = options.daysLeft === 1 ? 'tomorrow' : `in ${options.daysLeft ?? 2} days`;
    return {
      title: `${capitalized} due ${when}`,
      body: `A Director still needs your ${what} for ${month}. Tap to open Student Awards and send it.${closeText}`,
    };
  }
  return {
    title: `${capitalized} still needed`,
    body: `A Director still needs your ${what} for ${month}. Tap to open Student Awards and send it.${closeText}`,
  };
}
